/**
 * Phase 5 — Browser-only helper that grafts public-access probe
 * results onto an already-built {@link MigrationReportModel}.
 *
 * This module is intentionally lightweight so the wizard bundle does
 * not pull in the full render-model-builder graph (mapping types,
 * generator types, Velocity types) that is only used server-side.
 * The same logic lives in `render-model-builder.ts` for the
 * legacy/server build path; both are unit-tested.
 */

import type {
  MigrationReportModel,
  ProbeReasonCode,
  ReportSection,
  ReportTable,
  WarningRow,
} from '../../types/render-model.js';
import type {
  DocumentSection,
  DocumentTable,
  MigrationDocument,
} from '../../types/generator.js';
import type { ProbeTargetResult } from './probe/orchestrator.js';
import { probeOutcomeMessage } from './probe/types.js';

/**
 * Schema length limits, inlined to avoid pulling the full zod schema
 * graph into the wizard bundle. These MUST stay in sync with the
 * exported constants of the same name in `src/types/render-model.ts`.
 * The server-side `render-model-builder.ts` continues to use the
 * exported constants directly, so a drift would surface as a test
 * failure on the builder side.
 */
const MAX_CELL_TEXT = 4000;
const MAX_WARNING_TEXT = 2000;
const MAX_SECTION_BODY_TEXT = 16000;

/**
 * Returns a new model with a Public-Access Checks section appended
 * and probe-derived warning rows merged. The input model is not
 * mutated. If `results` is empty, returns the input reference.
 *
 * The constructed model is structurally identical to one produced by
 * the server-side builder (which IS schema-validated at the boundary);
 * we intentionally do not re-validate here to keep the wizard bundle
 * free of the full zod schema graph. All string fields are clipped to
 * their schema-mandated maxima below.
 */
export function attachProbeResultsToModel(
  model: MigrationReportModel,
  results: ProbeTargetResult[],
): MigrationReportModel {
  if (results.length === 0) return model;
  const alreadyHas = model.sections.some((s) => s.anchorId === 'public-access-checks');
  const sections = alreadyHas ? model.sections : [...model.sections, buildProbeSection(results)];

  const existing = model.warnings ?? [];
  const newWarnings: WarningRow[] = [];
  for (const probe of results) {
    if (probe.outcome === 'verified-public') continue;
    newWarnings.push({
      severity: probeSeverity(probe.outcome),
      componentRef: clip(
        probe.componentRefs.length > 0 ? probe.componentRefs.join(', ') : probe.url,
        400,
      ),
      message: clip(`${probeOutcomeMessage(probe.outcome)} (${probe.url})`, MAX_WARNING_TEXT),
      probeReasonCode: probe.outcome as ProbeReasonCode,
    });
  }
  const warnings = [...existing, ...newWarnings].slice(0, 2000);

  return { ...model, sections, warnings };
}

function probeSeverity(outcome: ProbeTargetResult['outcome']): WarningRow['severity'] {
  if (outcome === 'probe-disabled') return 'info';
  return 'manual-action';
}

/**
 * Group probe results by outcome (in proposal §7.8 enum order) and
 * render one table per non-empty group.
 */
function buildProbeSection(results: ProbeTargetResult[]): ReportSection {
  const ORDER: ProbeTargetResult['outcome'][] = [
    'verified-public',
    'requires-authentication',
    'not-found',
    'mixed-content-blocked',
    'internal-or-unroutable-host',
    'network-or-cors-blocked',
    'timeout',
    'unexpected-response',
    'probe-disabled',
  ];
  const groups = new Map<ProbeTargetResult['outcome'], ProbeTargetResult[]>();
  for (const r of results) {
    const list = groups.get(r.outcome) ?? [];
    list.push(r);
    groups.set(r.outcome, list);
  }
  const tables: ReportTable[] = [];
  for (const outcome of ORDER) {
    const rows = groups.get(outcome);
    if (!rows || rows.length === 0) continue;
    tables.push({
      title: clip(`${probeGroupLabel(outcome)} (${rows.length})`, 200),
      rows: rows.map((r) => ({
        parameter: clip(r.url, MAX_CELL_TEXT),
        value: clip(r.componentRefs.length > 0 ? r.componentRefs.join(', ') : '—', MAX_CELL_TEXT),
      })),
    });
  }
  return {
    title: 'Public-Access Checks',
    anchorId: 'public-access-checks',
    content: clip(
      'The assistant attempted an anonymous, unauthenticated check of each customer-facing endpoint referenced by the configuration. Per the strict success criterion, only endpoints returning a verified-public ArcGIS REST response are considered reachable by Velocity. Every other outcome requires manual confirmation; the rows below are grouped by cause so the appropriate remediation is clear.',
      MAX_SECTION_BODY_TEXT,
    ),
    tables,
  };
}

function probeGroupLabel(outcome: ProbeTargetResult['outcome']): string {
  switch (outcome) {
    case 'verified-public': return 'Verified anonymously reachable';
    case 'requires-authentication': return 'Requires authentication';
    case 'not-found': return 'Not found';
    case 'mixed-content-blocked': return 'Blocked by mixed-content policy';
    case 'internal-or-unroutable-host': return 'Internal or unroutable host';
    case 'network-or-cors-blocked': return 'Network or CORS blocked';
    case 'timeout': return 'Timed out';
    case 'unexpected-response': return 'Unexpected response';
    case 'probe-disabled': return 'Check skipped';
  }
}

export interface AttachProbeToDocumentOptions {
  /**
   * Whether public-access probes were enabled for this run. When
   * `false`, every result will be `probe-disabled` (the worker
   * short-circuits each target) and the appended section explains the
   * results are skipped by user request rather than by an internal
   * limit. Defaults to `true`.
   */
  probesEnabled?: boolean;
}

/**
 * Returns a new `MigrationDocument` with a "URL Verification Checks"
 * section appended that summarizes the public-access probe results.
 * The input document is not mutated. If `results` is empty, returns
 * the input reference unchanged.
 *
 * The downloadable HTML migration guide (rendered by
 * `src/docs/html-generator.ts:generateHtml`) walks `doc.sections`, so
 * the probe outcomes must live on the document for the offline guide
 * to surface them. The in-page interactive view also picks up this
 * section because `buildRenderModelFromDocument` adapts every document
 * section into the render model.
 *
 * When `options.probesEnabled === false`, the section title and intro
 * paragraph make it explicit that URL verification was disabled at the
 * operator's request and that each referenced endpoint requires manual
 * verification before relying on this migration plan.
 */
export function attachProbeResultsToDocument(
  doc: MigrationDocument,
  results: ProbeTargetResult[],
  options: AttachProbeToDocumentOptions = {},
): MigrationDocument {
  if (results.length === 0) return doc;
  const probesEnabled = options.probesEnabled !== false;
  const alreadyHas = doc.sections.some((s) => s.anchorId === 'public-access-checks');
  if (alreadyHas) return doc;
  const section = buildProbeDocumentSection(results, probesEnabled);
  return { ...doc, sections: [...doc.sections, section] };
}

function buildProbeDocumentSection(
  results: ProbeTargetResult[],
  probesEnabled: boolean,
): DocumentSection {
  const ORDER: ProbeTargetResult['outcome'][] = [
    'verified-public',
    'requires-authentication',
    'not-found',
    'mixed-content-blocked',
    'internal-or-unroutable-host',
    'network-or-cors-blocked',
    'timeout',
    'unexpected-response',
    'probe-disabled',
  ];
  const groups = new Map<ProbeTargetResult['outcome'], ProbeTargetResult[]>();
  for (const r of results) {
    const list = groups.get(r.outcome) ?? [];
    list.push(r);
    groups.set(r.outcome, list);
  }
  const tables: DocumentTable[] = [];
  for (const outcome of ORDER) {
    const rows = groups.get(outcome);
    if (!rows || rows.length === 0) continue;
    const label = outcome === 'probe-disabled' && !probesEnabled
      ? 'Not checked (URL verification disabled)'
      : probeGroupLabel(outcome);
    tables.push({
      title: clip(`${label} (${rows.length})`, 200),
      rows: rows.map((r) => ({
        parameter: clip(r.url, MAX_CELL_TEXT),
        value: clip(
          r.componentRefs.length > 0 ? r.componentRefs.join(', ') : '—',
          MAX_CELL_TEXT,
        ),
      })),
    });
  }
  const title = probesEnabled
    ? 'URL Verification Checks'
    : 'URL Verification Checks (Skipped)';
  const content = probesEnabled
    ? 'The assistant issued an anonymous, credential-less GET request to each customer-facing endpoint referenced by the configuration to confirm it still resolves. Per the strict success criterion, only endpoints that return a verified-public ArcGIS REST response are considered reachable. Every other outcome requires manual confirmation; the rows below are grouped by cause so the appropriate remediation is clear.'
    : 'URL verification was disabled on the Migration Options page for this run. The endpoints referenced by the configuration were NOT checked from your browser. Before relying on this migration plan, manually verify that each endpoint listed below is reachable from where ArcGIS Velocity will run, that it returns a valid ArcGIS REST response, and that the Velocity service account has the access it needs.';
  return {
    title,
    anchorId: 'public-access-checks',
    content: clip(content, MAX_SECTION_BODY_TEXT),
    tables,
  };
}

function clip(value: string, maxLength: number): string {
  // eslint-disable-next-line no-control-regex
  const cleaned = value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u2066-\u2069\u202A-\u202E]/g, '');
  return cleaned.length <= maxLength ? cleaned : cleaned.slice(0, maxLength);
}
